import { type FoundationVerticalDatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { FoundationVerticalDatumEntity } from "building-types/datums/datumEntityTypes";
import { FoundationDatumVerticalAnchor } from "building-types/datums/datumsTypesEnum";
import type { FloorPath } from "building-types/floors/floorTypes";
import type { SolidFoundationAssembly } from "building-types/foundations/foundationAssemblyEntityTypes";
import type {
  StemWallEntity,
  PierEntity,
  CircularIsolatedFootingEntity,
  RectangularIsolatedFootingEntity,
} from "building-types/foundations/foundationComponentEntityTypes";
import { PierType, SlabType, StemWallType } from "building-types/foundations/foundationEnums";
import type { FoundationPath, FoundationSectionSize } from "building-types/foundations/foundationTypes";
import { FoundationSectionType } from "building-types/foundations/foundationTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { SlabSettings } from "building-types/settings/foundationSettingsTypes";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { levelInchesToWorld } from "common/utils/nominalNumbers";
import { foundationVerticalDatumEntityNew } from "doc/datums/datumEntityNew";
import {
  isSpanFoundationCondition,
  isBasementFoundationCondition,
  isDeckFoundationCondition,
  isInteriorFoundationCondition,
  isGarageFoundationCondition,
  isPorchFoundationCondition,
} from "doc/utils/foundationSystemTools";
import { DIST_EPSILON_SQUARED } from "geom/core";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { vec2DistSquared } from "geom/vec2";
import { createDatumAbstraction } from "../datums/datumAbstractionFactory";
import { mergeDatumOffset } from "../datums/datumEntity";
import { isolatedFootingTop } from "./footing";
import {
  foundationPathOffsetAlignment,
  foundationSectionId,
  isolatedFoundationSectionSizeFromAlignment,
  voidFoundationSectionSizeNew,
} from "./foundationStrip";
import { foundationPathCoordinates } from "./pathCoordinates";
import { slabTypeAtFoundationPath } from "./slabUtils";

//if it is a stem wall assembly where pier is applied then pier top datum may be inherited based on stem wall type and foundation condition.
export function isolatedPierTop({
  foundationPath,
  level,
  slabSettings,
  isolatedAssembly,
  stemWall,
}: {
  foundationPath: Pick<FoundationPath, "condition" | "datums"> & Partial<Pick<FoundationPath, "assembly">>;
  level: Level;
  slabSettings: SlabSettings | undefined;
  isolatedAssembly: SolidFoundationAssembly;
  stemWall?: StemWallEntity;
}) {
  let topDatum = isolatedPierTopDatum({
    path: foundationPath,
    level,
    slabSettings,
    stemWall,
  });

  // these offset merges are added to support the existing behavior, were the offset can be obtined from
  // the isolatedPierTopDatum if not defined by the assembly override
  if (foundationPath.assembly?.topDatumAbstraction) {
    topDatum = mergeDatumOffset(foundationPath.assembly.topDatumAbstraction.getDatumEntity(), topDatum.offset);
    topDatum = mergeDatumOffset(topDatum, foundationPath.assembly.topDatumAbstraction.getDatumEntity().offset);
  } else if (isolatedAssembly.topDatumAbstraction) {
    topDatum = mergeDatumOffset(isolatedAssembly.topDatumAbstraction.getDatumEntity(), topDatum.offset);
    topDatum = mergeDatumOffset(topDatum, isolatedAssembly.topDatumAbstraction.getDatumEntity().offset);
  }

  const datumAbstraction = createDatumAbstraction(topDatum) as FoundationVerticalDatumAbstraction;

  let top = datumAbstraction.resolveVerticalDatum({
    level,
    path: foundationPath,
  });

  if (top > level.floorElevation + level.topOfWall) {
    top = levelInchesToWorld(level.topOfWall, level.floorElevation);
  }

  return top;
}

function isolatedPierTopDatum({
  level,
  path,
  slabSettings,
  stemWall,
}: {
  level: Level;
  path: Pick<FoundationPath, "condition">;
  slabSettings: SlabSettings | undefined;
  stemWall?: StemWallEntity;
}): FoundationVerticalDatumEntity {
  if (!slabSettings) {
    return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_FLOORPLATE });
  }
  const { condition } = path;
  const { interiorSlabType, basementSlabType } = slabSettings;
  const { floorPlateThickness } = level;

  if (isSpanFoundationCondition[condition]) {
    if (slabTypeAtFoundationPath({ condition }, slabSettings) === SlabType.NONE) {
      return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_FLOORPLATE });
    } else {
      return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
    }
  }

  if (basementSlabType !== SlabType.NONE && isBasementFoundationCondition[condition]) {
    return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.LEVEL_TOP_OF_WALL });
  }

  if (isDeckFoundationCondition[condition]) {
    return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_FLOORPLATE });
  }

  if (interiorSlabType === SlabType.NONE) {
    if (isInteriorFoundationCondition[condition]) {
      return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_FLOORPLATE });
    } else if (isGarageFoundationCondition[condition]) {
      return foundationVerticalDatumEntityNew({
        anchor: FoundationDatumVerticalAnchor.LEVEL_FLOOR_ELEVATION,
        offset: -floorPlateThickness,
      });
    } else if (isPorchFoundationCondition[condition]) {
      return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
    }
  }

  if (stemWall) {
    const { stemWallType } = stemWall;

    switch (stemWallType) {
      case StemWallType.CMU: {
        if (isInteriorFoundationCondition[condition]) {
          return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
        }
        break;
      }
      case StemWallType.CIP: {
        if (isInteriorFoundationCondition[condition]) {
          return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.LEVEL_FLOOR_ELEVATION });
        }
        break;
      }
      default: {
        assertUnreachable(stemWallType);
      }
    }
  }

  if (isInteriorFoundationCondition[condition]) {
    return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
  } else if (isGarageFoundationCondition[condition]) {
    return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.LEVEL_FLOOR_ELEVATION });
  } else if (isPorchFoundationCondition[condition]) {
    return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
  } else {
    return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
  }
}

export function makeIsolatedPierSize({
  level,
  foundationPath,
  floorPath,
  footing,
  pier,
  slabSettings,
  isolatedAssembly,
  stemWall,
  planarGraphs,
}: {
  level: Level;
  foundationPath: Pick<FoundationPath, "condition" | "subSystemOffset" | "datums" | "planarGraphId" | "halfEdgeIndex"> &
    Partial<Pick<FoundationPath, "assembly">>;
  floorPath?: Pick<FloorPath, "floorPriority">;
  footing: RectangularIsolatedFootingEntity | CircularIsolatedFootingEntity;
  pier: PierEntity;
  slabSettings: SlabSettings | undefined;
  isolatedAssembly: SolidFoundationAssembly;
  stemWall?: StemWallEntity;
  planarGraphs: Table<PolygonPlanarGraph>;
}): FoundationSectionSize {
  const sectionType = FoundationSectionType.COMPONENT;
  const { assemblyId } = isolatedAssembly;
  const sectionId = foundationSectionId(pier.componentType, sectionType, undefined, assemblyId);
  const [startX, startY, endX, endY] = foundationPathCoordinates(foundationPath, planarGraphs);

  if (vec2DistSquared(startX, startY, endX, endY) < DIST_EPSILON_SQUARED) {
    return voidFoundationSectionSizeNew(pier, sectionId);
  }

  let pierWidth = 0;
  const { pierType, material } = pier;
  let pierDepth;

  switch (pierType) {
    case PierType.RECTANGULAR_CIP:
    case PierType.RECTANGULAR_CMU:
      pierWidth = pier.pierWidth;
      pierDepth = pier.pierDepth;
      break;
    case PierType.CIRCULAR_CIP:
    case PierType.CIRCULAR_CMU:
      pierWidth = pier.pierRadius * 2;
      pierDepth = pier.pierDepth;
      break;
    case PierType.CUSTOM:
      //@TO-DO
      break;
    default:
      assertUnreachable(pierType);
  }

  const pierTop = isolatedPierTop({
    foundationPath,
    level,
    slabSettings,
    isolatedAssembly,
    stemWall,
  });

  const footingTop = isolatedFootingTop({
    foundationPath,
    level,
    footing,
    isolatedAssembly,
  });

  const { offset, alignment } = foundationPathOffsetAlignment({
    foundationPath,
    floorPath,
  });
  return isolatedFoundationSectionSizeFromAlignment({
    sectionComponent: pier,
    sectionType,
    sectionId,
    top: pierDepth && pierDepth > 0 ? footingTop + pierDepth : pierTop,
    depth: pierDepth && pierDepth > 0 ? pierDepth : pierTop - footingTop,
    width: pierWidth,
    alignment,
    assemblyId,
    offset,
    material,
    pickSource: isolatedAssembly.pickSource,
  });
}
